<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <div class="flex flex-col gap-y-5">
            <div>
              <Multiselect
                v-model="rawPointEntry.type"
                :error="errors?.type"
                :options="types"
                :label="$t('pointLedger.type.DEFAULT')"
                class="w-full"
              />
              <p v-if="showRankingWarning" class="pt-2 text-red">
                {{ $t('pages.driver.pointList.modal.warningText') }}
              </p>
            </div>
            <FormInput
              v-if="pointEntry?.id !== undefined"
              :model-value="pointEntry?.author?.fullName ?? ''"
              :label="$t('pointLedger.author.name')"
              class="w-full"
              :disabled="true"
            />
            <FormInput
              v-model="rawPointEntry.amount"
              :error="errors?.amount"
              class="col-start-1 col-end-2"
              type="number"
              :label="$t('pointLedger.amount')"
              :placeholder="$t('pointLedger.amount')"
            />
            <date-picker
              v-model="rawPointEntry.timestamp"
              :error="errors?.timestamp"
              :label="$t('pointLedger.timestamp')"
              mode="dateTime"
            />
            <div>
              <text-area
                v-model="rawPointEntry.comment"
                :error="errors?.comment"
                :label="$t('pointLedger.comment')"
                class="w-full"
              >
                <template #label>
                  {{ $t('pointLedger.comment') }}
                  <span class="font-normal">
                    {{ driverLanguageLabel }}
                  </span>
                </template>
              </text-area>
              <p class="pt-2 text-red">
                {{ $t('pages.driver.pointList.modal.warningTextComment') }}
              </p>
            </div>

          </div>
        </div>
        <div class="w-full text-center">
          <custom-button
            variant="primary"
            class="mt-15"
            :busy="loading"
            @click="save"
          >
            {{ $t('save') }}
          </custom-button>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  date, number, object, string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import FormInput from '@utils/inputs/Input.vue';
import DatePicker from '@components/utils/inputs/DatePicker.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { syncRef } from '@hooks/state';
import CustomButton from '@utils/CustomButton.vue';
import TextArea from '@utils/inputs/TextArea.vue';
import Multiselect from '@utils/Multiselect.vue';
import type { Driver, PointLedger } from '@/entities/Entities';
import { useStore } from '@/store';

interface Props {
  open: boolean;
  driver: Driver;
  pointEntry?: PointLedger;
}

const emit = defineEmits(['update:open', 'update:pointEntry', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalPointEntry = useVModel(props, 'pointEntry', emit);

const isNew = computed(() => !props.pointEntry);
const tKey = computed(() =>
  (isNew.value
    ? 'pages.driver.pointList.modal.new'
    : 'pages.driver.pointList.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const driver = computed(() => props.driver);

const types = [
  {
    label: t('pointLedger.type.admin'),
    value: 'admin',
  },
  {
    label: t('pointLedger.type.loyalty'),
    value: 'loyalty',
  },
];

const warningTypes = ['loyalty'];
const driverLanguageLabel = computed(() =>
  `(${t('pointLedger.driverLanguage')}: ${t(`language.${driver.value?.user?.language}`)})`);

const initialData = (pointEntry?: PointLedger) =>
  (pointEntry
    ? {
      ...pointEntry,
      author: pointEntry.author?.['@id'],
      timestamp: new Date(pointEntry.timestamp),
    }
    : {
      amount: undefined,
      author: undefined,
      timestamp: undefined,
      comment: undefined,
      type: undefined,
    });

const rawPointEntry = syncRef(internalPointEntry, (pointEntry) => ({
  ...initialData(pointEntry),
}));
const showRankingWarning = computed(() => warningTypes.includes(rawPointEntry.value.type));

const {
  data: resPointEntry,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    amount: number().nullable().required(t('validation.notEmpty')),
    timestamp: date().nullable().required(t('validation.notEmpty')),
    comment: string().nullable().required(t('validation.notEmpty')),
    type: string().nullable().required(t('validation.notEmpty')),
  }),
  rawPointEntry,
);

function resetData() {
  rawPointEntry.value = { ...initialData() } as unknown as typeof resPointEntry['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalPointEntry.value = undefined;
}

const [exec, { loading }] = useFetchAction();

const store = useStore();
const currentUser = computed(() => store.state?.auth?.user?.['@id'] ?? null);

const save = validatedFn(async () => {
  try {
    const edit = !!props.pointEntry;
    const url = edit ? props.pointEntry['@id'] : '/point_ledgers';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        amount: resPointEntry.value.amount,
        author: props.pointEntry?.id !== undefined ? undefined : currentUser.value,
        timestamp: resPointEntry.value.timestamp,
        comment: resPointEntry.value.comment,
        type: resPointEntry.value.type,
        driver: driver.value['@id'],
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>
